import { Add, Save } from '@mui/icons-material';
import { Divider, MenuItem, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CatalogPicker } from './CatalogPicker';
import { ServiceAvatar } from './ServiceAvatar';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';
import useDeviceIntegrations from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { useSettingsSection } from 'src/SettingsSection/Providers/SettingsSectionProvider';
import { IntegrationConfiguration } from 'src/DeviceIntegrationManagement/MockData/types';
import { getManufacturer, getServiceKnowledge } from '../Helper/integrationKnowledge';
import { ConfigInputType, ServiceIntegrationParameter } from '@ekkogmbh/apisdk';
import { keyToLabel } from 'src/Common/textTansformers';

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant={'caption'}
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

const stringifyConfigValues = (configuration: IntegrationConfiguration): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(configuration)) {
    if (value !== undefined) {
      values[key] = String(value);
    }
  }
  return values;
};

export const IntegrationFormDrawer = (): React.JSX.Element => {
  const { setDrawerOpen } = useSettingsSection();
  const { descriptions, selectedIntegration: providedIntegration, saveIntegration } = useDeviceIntegrations();
  const isEdit = providedIntegration !== undefined;

  const [serviceName, setServiceName] = useState<string | undefined>(providedIntegration?.serviceName);
  const [coordinate, setCoordinate] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const touch = (field: string): void => setTouched((prev) => ({ ...prev, [field]: true }));
  const setValue = (key: string, value: string): void => setValues((prev) => ({ ...prev, [key]: value }));

  const description = useMemo(() => descriptions.find((d) => d.name === serviceName), [descriptions, serviceName]);
  const [basicParameters, advancedParameters] = useMemo(() => {
    if (description === undefined) {
      return [[], []];
    }

    const basic: ServiceIntegrationParameter[] = [];
    const advanced: ServiceIntegrationParameter[] = [];

    description?.parameters.forEach((p) => {
      if (p.advanced || !p.required) {
        advanced.push(p);
      } else {
        basic.push(p);
      }
    });

    return [basic, advanced];
  }, [description]);

  const knowledge = useMemo(
    () => (serviceName !== undefined ? getServiceKnowledge(serviceName) : undefined),
    [serviceName],
  );

  const resetValues = useCallback(() => {
    setTouched({});

    setServiceName(providedIntegration?.serviceName);
    setCoordinate(providedIntegration?.coordinate ?? '');

    const config = providedIntegration?.configuration ?? {};
    setValues(stringifyConfigValues(config as Record<string, string | number | undefined>));
  }, [providedIntegration]);

  // (Re-)initialize whenever the drawer opens or switches target.
  useEffect(() => {
    resetValues();
  }, []);

  const isCredentialsOk = useMemo(
    () =>
      description?.parameters
        .filter((p) => p.required && p.type === ConfigInputType.PASSWORD)
        .every((p) => (values[p.key] ?? '') !== '') ?? false,
    [description, values],
  );

  const isValid = useMemo(() => {
    if (serviceName === undefined) {
      return false;
    }
    if (coordinate.trim() === '') {
      return false;
    }

    const requiredOk = description?.parameters
      .filter((field) => field.required)
      .every((field) => (values[field.key] ?? '').trim() !== '');
    return requiredOk && isCredentialsOk;
  }, [serviceName, coordinate, values, isCredentialsOk, description]);

  /**
   * Assembles config object from input values.
   * Erases empty inputs and converts types where necessary.
   */
  const configuration = useMemo(() => {
    if (description === undefined) {
      return undefined;
    }

    const config: IntegrationConfiguration = {};
    for (const p of description.parameters) {
      const raw = (values[p.key] ?? '').trim();

      if (!p.required && raw === '') {
        continue;
      }

      config[p.key] = [ConfigInputType.NUMBER || ConfigInputType.NUMBER_ZERO].includes(p.type) ? Number(raw) : raw;
    }

    /**
     * PDI specific workaround for split authentication schema
     * Moving password to the updatePassword key.
     * @TODO we may need a proper rule set for conditional parameter(-set)s
     */
    if (description.name.startsWith('PdiDigital') && config.pickingPassword !== undefined) {
      config.updatePassword = config.password;
      delete config.password;
    }

    return config;
  }, [description, values]);

  const onSave = useCallback(() => {
    if ([serviceName, coordinate, configuration].includes(undefined)) {
      return;
    }

    saveIntegration(serviceName!, coordinate, configuration!, () => setDrawerOpen(false));
  }, [coordinate, serviceName, configuration]);

  const renderField = (field: ServiceIntegrationParameter): React.JSX.Element => {
    const labelText = keyToLabel(field.key);
    if (field.type === ConfigInputType.ENUM) {
      return (
        <StyledSelectField
          key={field.key}
          label={labelText}
          value={values[field.key] ?? ''}
          onChange={(e) => setValue(field.key, e.target.value as string)}
        >
          {(field.options ?? []).map((option, i) => (
            <MenuItem key={i} value={option as string}>
              {option}
            </MenuItem>
          ))}
        </StyledSelectField>
      );
    }
    return (
      <StyledTextField
        key={field.key}
        label={field.required ? `${labelText} *` : labelText}
        type={field.type === ConfigInputType.PASSWORD ? 'password' : 'text'}
        numeric={[ConfigInputType.NUMBER, ConfigInputType.NUMBER_ZERO].includes(field.type)}
        value={values[field.key] ?? ''}
        onChange={(e) => setValue(field.key, e.target.value)}
        onBlur={() => touch(field.key)}
        error={field.required && touched[field.key] && (values[field.key] ?? '').trim() === ''}
        helperText={field.hint}
        inputProps={field.type === ConfigInputType.PASSWORD ? { autoComplete: 'new-password' } : undefined}
      />
    );
  };

  const showCatalog = !isEdit && serviceName === undefined;

  const contentHeader = useMemo(() => {
    const manufacturer = knowledge?.manufacturerId ? getManufacturer(knowledge.manufacturerId) : undefined;
    return (
      <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ minWidth: 0 }}>
        {serviceName && <ServiceAvatar serviceName={serviceName} size={36} />}
        <Stack spacing={0}>
          <Typography variant={'h6'} fontWeight={700} noWrap>
            {showCatalog
              ? 'Add integration'
              : isEdit
                ? `Edit ${knowledge?.displayName ?? serviceName ?? ''}`
                : `Connect ${knowledge?.displayName ?? serviceName ?? ''}`}
          </Typography>
          <Typography variant={'caption'} color={'text.secondary'} noWrap>
            {showCatalog ? 'Pick the system to connect to.' : manufacturer ? `by ${manufacturer.name}` : ''}
          </Typography>
        </Stack>
      </Stack>
    );
  }, [serviceName, knowledge]);

  const catalogContent = useMemo(
    () => (
      <SectionDrawerContent header={contentHeader}>
        <CatalogPicker onPick={setServiceName} />
      </SectionDrawerContent>
    ),
    [contentHeader],
  );

  const formContent = (
    <SectionDrawerContent
      header={contentHeader}
      onBack={!isEdit && !showCatalog ? () => setServiceName(undefined) : undefined}
      actions={{
        confirm: {
          onClick: () => {
            onSave();
          },
          icon: isEdit ? <Save /> : <Add />,
          disabled: !isValid,
          label: isEdit ? 'Save' : 'Connect',
        },
        reset: { onClick: resetValues },
      }}
    >
      <Stack spacing={4}>
        {/* Placement */}
        <Stack spacing={1.5}>
          <SectionHeader title={'Placement'} />
          <CoordinateInput
            disabled={isEdit}
            label={'Coordinate *'}
            value={coordinate}
            onChange={(c) => setCoordinate(c)}
            trailingDelimiter={false}
          />
        </Stack>

        {/* Connection */}
        {basicParameters.length > 0 ? (
          <Stack spacing={2}>
            <SectionHeader title={'Connection'} />
            {basicParameters.map(renderField)}
          </Stack>
        ) : (
          <Typography variant={'body2'} color={'text.secondary'}>
            This integration needs no connection configuration.
          </Typography>
        )}

        {/* Advanced */}
        {advancedParameters.length > 0 && (
          <Stack spacing={2}>
            <Divider />
            <SectionHeader title={'Advanced'} />
            {advancedParameters.map(renderField)}
          </Stack>
        )}
      </Stack>
    </SectionDrawerContent>
  );

  return showCatalog ? catalogContent : formContent;
};
